import React, { useEffect, useState } from "react";
import { X, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { getLastImport, deleteImport, downloadImportFile } from "../../../api/database";
import saveBlob from "../../../components/import/saveBlob";

/**
 * "Delete uploaded data" modal. The agent picks Owners or Buyers, then either
 * undoes the most recent upload for that view or clears ALL records in it.
 * A two-step confirm guards the destructive action.
 */
export default function DeleteImportModal({ initialView = "owners", onClose, onDone }) {
  const [view, setView] = useState(initialView === "buyers" ? "buyers" : "owners");
  const [info, setInfo] = useState(null); // { last_import, total_count }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'last' | 'all' | null

  const load = async (v) => {
    setLoading(true);
    setConfirm(null);
    try {
      const res = await getLastImport(v);
      setInfo(res.data);
    } catch {
      setInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(view);
  }, [view]);

  const run = async (mode) => {
    setBusy(true);
    try {
      const res = await deleteImport(view, mode);
      toast.success(res.data.detail || "Deleted");
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const last = info?.last_import;
  const total = info?.total_count ?? 0;
  const labelView = view === "owners" ? "owners" : "buyers";

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[520px] max-w-full flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Delete uploaded data</h3>
          <button onClick={onClose} className="text-gray-500">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-600 mb-3">
            Which data do you want to remove?
          </p>

          {/* Owners / Buyers picker */}
          <div className="inline-flex bg-gray-100 rounded-lg p-0.5 mb-4">
            {["owners", "buyers"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-6 py-1.5 rounded-md text-sm font-bold capitalize ${
                  view === v ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-3">
              {/* Delete last upload */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm font-bold text-gray-900 mb-0.5">Last upload</div>
                {last ? (
                  <div className="text-xs text-gray-500">
                    <span className="font-semibold text-gray-700">{last.filename}</span> —{" "}
                    {last.record_count} record{last.record_count !== 1 ? "s" : ""}
                    {/* The file as it was uploaded, so what went in can be checked. */}
                    {last.has_file && (
                      <>
                        {" · "}
                        <button
                          type="button"
                          className="text-[#0e7d86] font-semibold hover:underline"
                          onClick={async () => {
                            try {
                              saveBlob(await downloadImportFile(last.job_id), last.filename);
                            } catch (e) {
                              toast.error("Could not download the original file");
                            }
                          }}
                        >
                          Download original file
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400">No tracked upload for {labelView}.</div>
                )}
                {confirm === "last" ? (
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => run("last")}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold disabled:opacity-50"
                    >
                      {busy ? "Deleting…" : "Yes, delete it"}
                    </button>
                    <button onClick={() => setConfirm(null)} className="text-xs font-semibold text-gray-500">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirm("last")}
                    disabled={!last}
                    className="mt-2 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-600 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40"
                  >
                    <Trash2 size={13} /> Delete last upload
                  </button>
                )}
              </div>

              {/* Clear all */}
              <div className="border border-rose-100 bg-rose-50/50 rounded-lg p-3">
                <div className="text-sm font-bold text-gray-900 mb-0.5 flex items-center gap-1.5">
                  <AlertTriangle size={14} className="text-rose-500" /> Clear ALL {labelView}
                </div>
                <div className="text-xs text-gray-500">
                  Permanently deletes every {view === "owners" ? "owner" : "buyer"} record you have
                  ({total} total). This can’t be undone.
                </div>
                {confirm === "all" ? (
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => run("all")}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold disabled:opacity-50"
                    >
                      {busy ? "Deleting…" : `Yes, delete all ${total}`}
                    </button>
                    <button onClick={() => setConfirm(null)} className="text-xs font-semibold text-gray-500">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirm("all")}
                    disabled={!total}
                    className="mt-2 px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40"
                  >
                    <Trash2 size={13} /> Clear all {labelView}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm font-semibold text-gray-600">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
